import { Box, Button, Flex, HStack, Icon, Link, Spinner, Text, useToast, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { FiCheckCircle, FiXCircle, FiAlertCircle, FiUsers, FiLink, FiCopy, FiExternalLink, FiFileText, FiShield, FiAlertTriangle, FiDownload } from "react-icons/fi";

// Eksplorer blockchain — publiczna weryfikacja głosowania (działa dla każdego: tajne i jawne).
const EXPLORER_URL = process.env.REACT_APP_EXPLORER_URL || "http://localhost:12000/explorer";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
import { VotingResults, VotingBlockchainInfo, VotingVerification, getVotingVerification, QuestionResult, ResultOutcome, convertHtmlToText, htmlToPlainText } from "@lubbezposrednio-frontend/core";
import { DonutChart } from "../../components/charts/DonutChart";

const CodeField: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const toast = useToast();
  return (
    <Box>
      <Text fontSize="12px" fontWeight="700" color="gray.500" mb="4px">
        {label}
      </Text>
      <Flex align="center" gap="8px" bg="rgba(67,0,152,0.05)" borderRadius="10px" p="9px 11px">
        <Text fontSize="12px" fontFamily="mono" color="primary" noOfLines={1} flex="1" wordBreak="break-all">
          {value}
        </Text>
        <Icon
          as={FiCopy}
          color="gray.500"
          boxSize="14px"
          cursor="pointer"
          flexShrink={0}
          onClick={() => {
            navigator.clipboard?.writeText(value);
            toast({ title: "Skopiowano", status: "success", duration: 1200, isClosable: true });
          }}
        />
      </Flex>
    </Box>
  );
};

const Pill: React.FC<{ children: React.ReactNode; tone?: "ok" | "muted" }> = ({ children, tone = "muted" }) => (
  <Flex
    align="center"
    gap="5px"
    fontSize="11px"
    fontWeight="700"
    px="10px"
    h="24px"
    borderRadius="999px"
    bg={tone === "ok" ? "rgba(47,206,37,0.12)" : "rgba(67,0,152,0.07)"}
    color={tone === "ok" ? "#1f9e18" : "primary"}
  >
    {children}
  </Flex>
);

const TYPE_LABELS: Record<string, string> = { yes_no: "TAK / NIE", single: "Jeden wybór", multiple: "Wiele wyborów" };

const outcomeBadge = (o: ResultOutcome): { label: string; color: string; bg: string; icon: React.ElementType } => {
  switch (o.kind) {
    case "passed":
      return { label: "Przyjęto", color: "#1f9e18", bg: "rgba(47,206,37,0.12)", icon: FiCheckCircle };
    case "rejected":
      return { label: "Odrzucono", color: "#e51f44", bg: "rgba(229,31,68,0.08)", icon: FiXCircle };
    case "winner":
      return { label: "Zwycięzca", color: "#1f9e18", bg: "rgba(47,206,37,0.12)", icon: FiCheckCircle };
    case "passed_options":
      return { label: "Przyjęte opcje", color: "#1f9e18", bg: "rgba(47,206,37,0.12)", icon: FiCheckCircle };
    case "ranking":
      return { label: "Ranking", color: "#2563eb", bg: "rgba(37,99,235,0.10)", icon: FiUsers };
    default:
      return { label: "Brak rozstrzygnięcia", color: "#b7791f", bg: "rgba(237,137,54,0.14)", icon: FiAlertCircle };
  }
};

const AnswerBar: React.FC<{ content: string; count: number; percent: number; highlight?: boolean }> = ({ content, count, percent, highlight }) => {
  const [w, setW] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setW(Math.min(percent, 100)), 80);
    return () => clearTimeout(t);
  }, [percent]);

  return (
    <Box>
      <Flex justify="space-between" mb="4px">
        <Text fontSize="14px" fontWeight={highlight ? "700" : "500"} color={highlight ? "primary" : "gray.700"} noOfLines={1}>
          {content}
        </Text>
        <Text fontSize="13px" color="gray.500" flexShrink={0} ml="10px">
          {count} ({percent}%)
        </Text>
      </Flex>
      <Box h="8px" bg="rgba(67,0,152,0.08)" borderRadius="full" overflow="hidden">
        <Box h="100%" w={`${w}%`} bg={highlight ? "primary" : "rgba(67,0,152,0.45)"} borderRadius="full" transition="width 0.85s cubic-bezier(0.22,1,0.36,1)" />
      </Box>
    </Box>
  );
};

const QuestionCard: React.FC<{ question: QuestionResult; order: number }> = ({ question, order }) => {
  const badge = outcomeBadge(question.outcome);
  const winnerId = question.outcome.kind === "winner" ? question.outcome.winner_id : undefined;
  const passedSet = new Set(question.outcome.kind === "passed_options" ? question.outcome.passed ?? [] : []);

  const isYesNo = question.qtype === "yes_no" && question.answers.length === 2;
  const ynColor = (content: string, i: number) => (/tak/i.test(content) ? "#2fce25" : /nie/i.test(content) ? "#e51f44" : ["#430098", "#a45cff"][i % 2]);
  const ynSegments = question.answers.map((a, i) => ({ value: a.percent, color: ynColor(a.content, i) }));
  const bars = question.answers.map(a => (
    <AnswerBar key={a.id} content={htmlToPlainText(a.content)} count={a.count} percent={a.percent} highlight={a.id === winnerId || passedSet.has(a.content)} />
  ));

  return (
    <Box bg="white" borderRadius="18px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p={{ base: "18px", md: "22px" }}>
      <Flex direction={{ base: "column", sm: "row" }} align={{ base: "stretch", sm: "flex-start" }} justify="space-between" mb="14px" gap="10px">
        <HStack align="flex-start" spacing="10px" minW={0}>
          <Flex w="26px" h="26px" borderRadius="full" bg="primary" color="white" align="center" justify="center" fontWeight="800" fontSize="13px" flexShrink={0}>
            {order}
          </Flex>
          <Box minW={0}>
            <Text fontWeight="700" color="primary" wordBreak="break-word">
              {convertHtmlToText(question.content)}
            </Text>
            <Text fontSize="12px" color="gray.400" mt="2px">
              {TYPE_LABELS[question.qtype] ?? question.qtype} · {question.total_votes} głosujących
            </Text>
          </Box>
        </HStack>
        <HStack spacing="6px" bg={badge.bg} color={badge.color} px="10px" py="5px" borderRadius="full" flexShrink={0} maxW="100%" minW={0} alignSelf={{ base: "flex-start", sm: "flex-start" }}>
          <Icon as={badge.icon} boxSize="14px" flexShrink={0} />
          <Text fontSize="12px" fontWeight="700" noOfLines={1}>
            {badge.label}
          </Text>
        </HStack>
      </Flex>

      {isYesNo ? (
        <Flex gap={{ base: "18px", md: "24px" }} align="center" direction={{ base: "column", sm: "row" }}>
          <DonutChart segments={ynSegments} size={108} thickness={14}>
            <Text fontSize="15px" fontWeight="800" color="primary" lineHeight="1">
              {question.total_votes}
            </Text>
            <Text fontSize="10px" color="gray.400">
              głosów
            </Text>
          </DonutChart>
          <VStack align="stretch" spacing="12px" flex="1" w="100%">
            {bars}
          </VStack>
        </Flex>
      ) : (
        <VStack align="stretch" spacing="12px">
          {bars}
        </VStack>
      )}
    </Box>
  );
};

/** Wspólna treść wyników — karta ważności + werdykty pytań. Używana w panelu i na publicznym ekranie zakończonego. */
// Pełna karta werdyktu: liczy teraz vs odczyt z łańcucha, hashe obok siebie, pobranie PDF, linki.
const VerificationCard: React.FC<{ votingId?: string; fallback?: VotingBlockchainInfo }> = ({ votingId, fallback }) => {
  const [v, setV] = useState<VotingVerification | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!votingId) {
      setLoading(false);
      return;
    }
    let alive = true;
    getVotingVerification(votingId)
      .then(r => alive && setV(r.data))
      .catch(() => alive && setV(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [votingId]);

  if (loading) {
    return (
      <Box bg="white" borderRadius="18px" border="1px solid rgba(67,0,152,0.08)" p="22px">
        <HStack spacing="10px">
          <Spinner size="sm" color="primary" thickness="2px" />
          <Text color="gray.500" fontSize="14px">Weryfikuję na blockchainie…</Text>
        </HStack>
      </Box>
    );
  }

  if (!v || !v.anchored) {
    if (fallback?.pending) {
      return (
        <Box bg="white" borderRadius="18px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p={{ base: "18px", md: "22px" }}>
          <Text fontSize="14px" color="gray.500">Kotwiczenie wyniku na blockchainie w toku — werdykt pojawi się wkrótce.</Text>
        </Box>
      );
    }
    return null;
  }

  const ok = v.match;
  const accent = ok ? "#1f9e18" : "#E51F44";
  const tintBg = ok ? "rgba(47,206,37,0.10)" : "rgba(229,31,68,0.08)";
  const borderC = ok ? "rgba(47,206,37,0.30)" : "rgba(229,31,68,0.35)";
  const protocolHref = v.protocol_path ? `${BACKEND_URL}${v.protocol_path}?download=1` : null;

  return (
    <Box bg="white" borderRadius="18px" border={`1px solid ${borderC}`} boxShadow="0 6px 20px rgba(67,0,152,0.05)" p={{ base: "18px", md: "22px" }}>
      <HStack spacing="12px" align="flex-start" mb="12px">
        <Flex w="42px" h="42px" borderRadius="13px" bg={tintBg} align="center" justify="center" flexShrink={0}>
          <Icon as={ok ? FiCheckCircle : FiAlertTriangle} color={accent} boxSize="22px" />
        </Flex>
        <Box flex="1" minW={0}>
          <Text fontWeight="800" color={ok ? "#1f7a16" : "accent"} fontSize="16px" lineHeight="1.25">
            {ok ? "Wynik zgodny z blockchainem" : "Niezgodność z blockchainem"}
          </Text>
          <Text fontSize="13px" color="gray.600" mt="2px">
            {ok
              ? "Zbiorczy odcisk policzony teraz jest identyczny z zapisanym w łańcuchu — wynik nie został zmieniony."
              : "Świeżo policzony odcisk różni się od zapisu w łańcuchu. Dane mogły zostać naruszone — zgłoś to organizatorowi."}
          </Text>
        </Box>
      </HStack>

      <HStack spacing="8px" mb="14px" flexWrap="wrap">
        <Pill tone="ok"><Icon as={FiShield} boxSize="11px" /> Zakotwiczone</Pill>
        {v.block_time && <Pill>Blok z {new Date(v.block_time).toLocaleString("pl-PL")}</Pill>}
      </HStack>

      <VStack align="stretch" spacing="10px">
        {v.recomputed_hash && <CodeField label="Odcisk policzony teraz (SHA-512)" value={v.recomputed_hash} />}
        {v.on_chain_hash && <CodeField label="Odcisk odczytany z łańcucha (HH)" value={v.on_chain_hash} />}
      </VStack>

      <HStack spacing="10px" mt="14px" flexWrap="wrap">
        {protocolHref && (
          <Button as={Link} href={protocolHref} isExternal leftIcon={<FiDownload />} bg="primary" color="white" borderRadius="11px" fontWeight="700" size="sm" _hover={{ bg: "#36007a", textDecoration: "none" }}>
            Pobierz protokół PDF
          </Button>
        )}
        {v.tx_url && (
          <Button as={Link} href={v.tx_url} isExternal leftIcon={<FiLink />} variant="outline" borderColor="primary" color="primary" borderRadius="11px" fontWeight="700" size="sm" _hover={{ bg: "rgba(67,0,152,0.06)", textDecoration: "none" }}>
            Transakcja w eksploratorze
          </Button>
        )}
        {v.explorer_url && (
          <Button as={Link} href={v.explorer_url} isExternal leftIcon={<FiExternalLink />} variant="outline" borderColor="primary" color="primary" borderRadius="11px" fontWeight="700" size="sm" _hover={{ bg: "rgba(67,0,152,0.06)", textDecoration: "none" }}>
            Pełna weryfikacja
          </Button>
        )}
      </HStack>
    </Box>
  );
};

export const VotingResultsContent: React.FC<{ results: VotingResults; votingId?: string }> = ({ results, votingId }) => {
  const valid = results.valid;
  const hasTurnout = results.turnout_percent != null;

  return (
    <VStack align="stretch" spacing="16px">
      <Box bg={valid ? "rgba(47,206,37,0.06)" : "rgba(229,31,68,0.05)"} border="1px solid" borderColor={valid ? "rgba(47,206,37,0.3)" : "rgba(229,31,68,0.25)"} borderRadius="18px" p={{ base: "20px", md: "24px" }}>
        <Flex gap={{ base: "18px", md: "26px" }} align="center" direction={{ base: "column", sm: "row" }}>
          {hasTurnout && (
            <DonutChart segments={[{ value: results.turnout_percent as number, color: valid ? "#2fce25" : "#e51f44" }]} size={124} thickness={13}>
              <Text fontSize="27px" fontWeight="800" color={valid ? "#1f9e18" : "#e51f44"} lineHeight="1">
                {results.turnout_percent}%
              </Text>
              <Text fontSize="11px" color="gray.500" fontWeight="600">
                frekwencja
              </Text>
            </DonutChart>
          )}
          <Box flex="1" minW={0} textAlign={{ base: "center", sm: "left" }}>
            <HStack spacing="11px" mb="6px" justify={{ base: "center", sm: "flex-start" }}>
              <Icon as={valid ? FiCheckCircle : FiXCircle} boxSize="28px" color={valid ? "#1f9e18" : "#e51f44"} />
              <Text fontSize="20px" fontWeight="800" color={valid ? "#1f9e18" : "#e51f44"}>
                {valid ? "Głosowanie ważne" : "Głosowanie nieważne"}
              </Text>
            </HStack>
            <Text fontSize="14px" color="gray.600">
              {hasTurnout ? `${results.voted_count} z ${results.eligible_count} uprawnionych oddało głos` : `Oddano ${results.voted_count} głosów`}
            </Text>
            {results.quorum_percent != null ? (
              <Text fontSize="13px" color="gray.500" mt="3px">
                Wymagane kworum {results.quorum_percent}% — {results.quorum_met ? "spełnione ✓" : "niespełnione ✗"}
              </Text>
            ) : (
              <Text fontSize="13px" color="gray.500" mt="3px">
                Bez wymaganego kworum
              </Text>
            )}
          </Box>
        </Flex>
      </Box>

      {results.questions.map((q, i) => (
        <QuestionCard key={q.id} question={q} order={i + 1} />
      ))}

      <VerificationCard votingId={votingId} fallback={results.blockchain} />
    </VStack>
  );
};
